import styles from "./DeliverySlotsChecker.module.scss";
import PostcodeForm from "./components/PostcodeForm/PostcodeForm";
import DeliverySlots from "./components/DeliverySlots/DeliverySlots";
import { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";
import {
  CachedSlotResult,
  EarliestAvailableSlots,
} from "./EarliestAvailableSlots";
import {
  getCookiePostcode,
  setCookiePostcode,
} from "./utils/deliveryCookieHelper";
import { getAvailableSlotOptions } from "./clients/availableSlotsService";
import { AvailableSlotRequest } from "./clients/AvailableSlotsRequest";
import { DeliveryCheckerEvents } from "./events/ListenEvents";
import { dispatchMonetateQEvent } from "../../utils/monetate/monetateQ";

type Props = {
  availableSlotsBaseUrl: string;
  skuId: string;
  variantPrice: string;
};

export const REQUEST_TIMEOUT = 13000;
const invalidPostcodeError = "Please enter a valid UK postcode";

const validatePostcode = (input: string): boolean => {
  return /^[A-Za-z]{1,2}\d[A-Za-z\d]? ?\d[A-Za-z]{2}$/.test(input.trim());
};

export const DeliverySlotsCheckerUI = ({
  availableSlotsBaseUrl,
  skuId,
  variantPrice,
}: Props) => {
  const [postcodeField, setPostcodeField] = useState<string>(
    getCookiePostcode() || "",
  );
  const [postcodeValid, setPostcodeValid] = useState(false);
  const [invalidPostcodeMessage, setInvalidPostcodeMessage] = useState<
    string | null
  >();
  const [infoMessage, setInfoMessage] = useState<string | undefined>();
  const [earliestSlots, setEarliestSlots] =
    useState<EarliestAvailableSlots | null>();
  const [buttonEnabled, setButtonEnabled] = useState<boolean>(false);
  const [requestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [services, setServices] = useState<string[]>([]);
  const [cache, setCache] = useState<Array<CachedSlotResult>>([]);

  const callAvailableSlotsApi = (
    postcode: string,
    services: string[],
  ): Promise<EarliestAvailableSlots> => {
    const request: AvailableSlotRequest = {
      typeName: "SimpleProduct",
      skuId: skuId,
      skuPrice: variantPrice,
      postcode: postcode,
      services: services,
    };
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => {
      timeoutController.abort("timeout");
    }, REQUEST_TIMEOUT);
    const onComplete = () => clearTimeout(timeoutId);

    return getAvailableSlotOptions(
      availableSlotsBaseUrl,
      request,
      timeoutController,
      onComplete,
    );
  };

  const getCachedSlotResult = (postcode: string, services: string[]) => {
    return cache.find(
      (result) =>
        result.postcode.trim().toUpperCase() ===
          postcode.trim().toUpperCase() &&
        result.services.length === services.length &&
        result.services.every((el) => services.includes(el)),
    );
  };

  const onTyping = (inputValue: string) => {
    clearResults();
    setPostcodeValid(false);
    setPostcodeField(inputValue);
    if (invalidPostcodeMessage) {
      if (!validatePostcode(inputValue)) {
        setInvalidPostcodeMessage(invalidPostcodeError);
        setButtonEnabled(false);
      } else {
        setInvalidPostcodeMessage(null);
        setButtonEnabled(true);
      }
    } else {
      setButtonEnabled(true);
    }
    if (!isEmpty(earliestSlots)) clearResults();
  };

  const clearResults = () => {
    setEarliestSlots(null);
    setInvalidPostcodeMessage(null);
    setInfoMessage(undefined);
  };

  const cacheAndSetEarliestSlots = (earliestSlots: EarliestAvailableSlots) => {
    setEarliestSlots(earliestSlots);

    if (earliestSlots.error) {
      return;
    }

    setCache((existing) => {
      const cacheEntry: CachedSlotResult = {
        postcode: postcodeField,
        services,
        slotOptions: earliestSlots.slotOptions || [],
      };
      return [...existing, cacheEntry];
    });
  };

  const runPostcodeCheck = async () => {
    setPostcodeField(postcodeField.trim().toUpperCase());
    if (validatePostcode(postcodeField)) {
      setCookiePostcode(postcodeField);
      setPostcodeValid(true);
      setRequestInProgress(true);

      const cachedResult = getCachedSlotResult(postcodeField, services);
      if (cachedResult) {
        setEarliestSlots(cachedResult);
      } else {
        cacheAndSetEarliestSlots(
          await callAvailableSlotsApi(postcodeField, services),
        );
      }

      setRequestInProgress(false);
      setButtonEnabled(false);
      dispatchMonetateQEvent(
        DeliveryCheckerEvents.DELIVERY_CHECKER_CHECK_PRESSED,
      );
    } else {
      setInvalidPostcodeMessage(invalidPostcodeError);
      setButtonEnabled(false);
    }
  };

  const handleServicesChange = (event: CustomEventInit) => {
    const { selectedDmsIds } = event.detail;
    clearResults();
    setButtonEnabled(true);
    setServices(selectedDmsIds);
  };

  useEffect(() => {
    if (earliestSlots && !earliestSlots.slotOptions && !earliestSlots.error) {
      setInfoMessage(
        "Please view delivery options for this postcode at checkout",
      );
    }
    if (earliestSlots?.error?.customerCanRetry) {
      setButtonEnabled(true);
    }
  }, [earliestSlots, postcodeField]);

  useEffect(() => {
    const prefetch = async () => {
      const cookiePostcode = getCookiePostcode();
      if (cookiePostcode !== null) {
        if (validatePostcode(cookiePostcode)) {
          setPostcodeValid(true);
          setRequestInProgress(true);
          const earliestAvailableSlots = await callAvailableSlotsApi(
            cookiePostcode,
            [],
          );
          if (!earliestAvailableSlots.error) {
            setEarliestSlots(earliestAvailableSlots);
          } else {
            setButtonEnabled(true);
            setPostcodeValid(false);
          }
          setRequestInProgress(false);
        }
      }
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(prefetch, { timeout: 1000 });
    } else {
      // fall back if the browser does not support requestIdleCallback
      prefetch();
    }
  }, []);

  useEffect(() => {
    window.addEventListener(
      DeliveryCheckerEvents.DELIVERY_SLOTS_CHECKER_SERVICES_CHANGED,
      handleServicesChange,
    );

    return () => {
      window.removeEventListener(
        DeliveryCheckerEvents.DELIVERY_SLOTS_CHECKER_SERVICES_CHANGED,
        handleServicesChange,
      );
    };
  }, []);

  return (
    <div className={styles.DeliverySlotChecker}>
      <PostcodeForm
        onChange={onTyping}
        value={postcodeField}
        onSubmit={runPostcodeCheck}
        isValidCode={postcodeValid}
        invalidPostcodeMessage={invalidPostcodeMessage}
        availableSlotsErrorMessage={earliestSlots?.error?.message}
        infoMessage={infoMessage}
        buttonEnabled={buttonEnabled}
        isRequestInProgress={requestInProgress}
      />
      {earliestSlots?.slotOptions && (
        <>
          <div
            id="delivery-slots-feedback"
            role="region"
            aria-live="polite"
            data-testid={"postcode-delivery-slots"}
          >
            <DeliverySlots slots={earliestSlots.slotOptions} />
          </div>
          <p className={styles.DeliverySlotChecker__title}>
            All delivery options available at checkout
          </p>
        </>
      )}
    </div>
  );
};
